import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, isDevMode, LOCALE_ID, NgModule} from '@angular/core';
import {BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {ConnectivityBannerComponent} from './components/connectivity-banner/connectivity-banner.component';
import {PwaInstallBannerComponent} from './components/pwa-install-banner/pwa-install-banner.component';
import {SafeHtmlPipe} from './pipes/safe-html.pipe';
import {ConfirmDialogService} from './services/confirm-dialog-service/confirm-dialog.service';
import {MatDialogModule} from '@angular/material/dialog';
import {ConfirmDialogComponent} from './services/confirm-dialog-service/confirm-dialog/confirm-dialog.component';
import {GoogleMapsComponent} from './components/google-maps/google-maps.component';
import {GoogleMapsModule} from '@angular/google-maps';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {provideAnimations} from '@angular/platform-browser/animations'; // async variant zorgde voor render problemen met datepicker
import {DataConverterComponent} from './components/data-converter/data-converter.component';
import {SvgMapComponent} from './components/svg-map/svg-map.component';
import {ApiService} from './services/api/api.service';
import {CommonModule, DatePipe, registerLocaleData} from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import {LinkLoginComponent} from './components/auth/link-login/link-login.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {FieldErrorComponent} from './components/field-error/field-error.component';
import {AuthenticationService} from './services/auth/authentication.service';
import {CodaltInterceptor} from './codalt.interceptor';
import {MatButton, MatButtonModule} from '@angular/material/button';
import {CodaltComponent} from './components/codalt/codalt.component';
import {NgLabelTemplateDirective, NgOptionComponent, NgOptionTemplateDirective, NgSelectComponent} from '@ng-select/ng-select';
import {CustomerService} from './services/customer.service';
import {ConvertGeometryPipe} from './pipes/convert-geometry.pipe';
import {EntitiesComponent} from './components/entities/entities.component';
import {SafeSrcPipe} from './pipes/safe-src.pipe';
import {WorkordersComponent} from './components/workorders/workorders.component';
import {MatListModule} from '@angular/material/list';
import {FilterPipe} from './pipes/filter.pipe';
import {UsersComponent} from './components/users/users.component';
import {MatTableModule} from '@angular/material/table';
import {MatPaginatorIntl, MatPaginatorModule} from '@angular/material/paginator';
import {MatSortModule} from '@angular/material/sort';
import {getDutchPaginatorIntl} from './dutch-paginator-intl';
import {UserEditComponent} from './components/user-edit/user-edit.component';
import {HasRolePipe} from './pipes/has-role.pipe';
import {AuthRolesComponent} from './components/auth/auth-roles/auth-roles.component';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {UserFieldDialogComponent} from './components/auth/auth-roles/user-field-dialog/user-field-dialog.component';
import {SvgOverlayComponent} from './components/google-maps/svg-overlay/svg-overlay.component';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatChip} from '@angular/material/chips';
import {ConvertPolygonPipe} from './pipes/convert-polygon.pipe';
import {MatIconModule} from '@angular/material/icon';
import {SvgAsImgComponent} from './components/svg-as-img/svg-as-img.component';
import {ByIdPipe} from './pipes/by-id.pipe';
import {ProjectsMapComponent} from './components/projects-map/projects-map.component';
import {GeoLatLngPipe} from './pipes/geo-lat-lng.pipe';
import {MatMenuModule} from '@angular/material/menu';
import {HasFeaturePipe} from './pipes/has-feature.pipe';
import {IndeterminatePipe} from './pipes/indeterminate.pipe';
import {MarkerPipe} from './pipes/marker.pipe';
import {CustomMarkerPipe} from './pipes/custom-marker.pipe';
import {IntersectsPipe} from './pipes/intersects.pipe';
import {EntityInBoundsPipe} from './pipes/entity-in-bounds.pipe';
import {TtmTicketService} from './services/ttm-ticket.service';
import {TtmTicketDialogComponent} from './components/ttm-ticket-dialog/ttm-ticket-dialog.component';
import {MatRadioModule} from '@angular/material/radio';
import {DrawingsComponent} from './components/drawings/drawings.component';
import {TtmTicketTypePipe} from './pipes/ttm-ticket-type.pipe';
import {TtmDetailsDialogComponent} from './components/ttm-details-dialog/ttm-details-dialog.component';
import {ProjectService} from './services/project.service';
import {TtmTicketsComponent} from './components/ttm-tickets/ttm-tickets.component';
import {MatTooltip} from '@angular/material/tooltip';
import {ImportDrawingDialogComponent} from './components/drawings/import-drawing-dialog/import-drawing-dialog.component';
import {MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatOption, MatRipple} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatBadge} from '@angular/material/badge';
import {PresentTtmTypesPipe} from './pipes/present-ttm-types.pipe';
import {PresentTtmStatusesPipe} from './pipes/present-ttm-statuses.pipe';
import {CountEnabledStatusesPipe} from './pipes/count-checkboxes-enabled.pipe';
import {CountEnabledTtmTypesPipe} from './pipes/count-enabled-ttm-types.pipe';
import {EntityZoomPipe} from './pipes/entity-zoomlevel.pipe';
import {ClusterTtmsPipe} from './pipes/cluster-ttms.pipe';
import {EntityService} from './services/entity.service';
import {TtmDetailsComponent} from './components/ttm-details/ttm-details.component';
import {CodaltImageComponent} from './components/codalt-image/codalt-image.component';
import {SafeBgUrlPipe} from './pipes/safe-bg-url.pipe';
import {register} from 'swiper/element/bundle';
import {ImageViewerComponent} from './components/image-viewer/image-viewer.component';
import {TtmSvgViewerComponent} from './components/ttm-svg-viewer/ttm-svg-viewer.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ServiceWorkerModule} from '@angular/service-worker';
import {TtmMergeComponent} from './components/ttm-merge/ttm-merge.component';
import {WorkorderDetailComponent} from './components/workorder-detail/workorder-detail.component';
import {WorkorderService} from './services/workorder.service';
import {CodaltDatePipe} from './pipes/codalt-date.pipe';
import {FileUploadFailDialogComponent} from './components/file-upload-fail-dialog/file-upload-fail-dialog.component';
import {MapMutatorComponent} from './components/google-maps/map-mutator/map-mutator.component';
import {TextOverlayComponent} from './components/google-maps/text-overlay/text-overlay.component';
import {WhereInPipe} from './pipes/where-in.pipe';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {CountEnabledFgItemsPipe} from './pipes/count-enabled-fg-items.pipe';
import {TtmStatusPipe} from './pipes/ttm-status.pipe';
import {CustomErrorHandlerService} from './custom-error-handler-service';
import {CanDeactivate} from './can-deactivate';
import {TtmStatusDialogComponent} from './components/ttm-status-dialog/ttm-status-dialog.component';
import {WorkorderComposeComponent} from './components/workorder-compose/workorder-compose.component';
import {TSNedMarkerPipe} from './pipes/tnsed-marker.pipe';
import {DecimalInputDirective} from './directives/decimal-input.directive';
import {DrawingNamePipe} from './pipes/drawing-name.pipe';
import {OverrideValuesPipe} from './pipes/override-values.pipe';
import {TtmHistoryDialogComponent} from './components/ttm-history-dialog/ttm-history-dialog.component';
import {GenericModule} from './components/generic/generic.module';
import {TtmSplitComponent} from './components/ttm-divide/ttm-split.component';
import {DashedLinePipe} from './pipes/dashed-line.pipe';
import {GndLinePipe} from './pipes/gnd-line.pipe';
import {CountAmountOfTtmsPipe} from './pipes/count-amount-of-ttms.pipe';
import {Nl2brPipe} from './pipes/nl2br.pipe';
import {ChangeTtmAmountDialogComponent} from './components/change-ttm-amount-dialog/change-ttm-amount-dialog.component';
import {TtmTicketDepartmentPipe} from './pipes/ttm-ticket-department.pipe';
import {TtmTicketStatusPipe} from './pipes/ttm-ticket-status.pipe';
import {IsDateBeforePipe} from './pipes/is-date-before.pipe';
import {IfNotNullPipe} from './pipes/if-not-null.pipe';
import {OrderDrawingsPipe} from './pipes/order-drawings.pipe';
import {ExportInterfaceComponent} from './components/export-interface/export-interface.component';
import {UserInsightComponent} from './components/user-insight/user-insight.component';
import {SumPipe} from './pipes/sum.pipe';
import {TtmTicketDetailComponent} from './components/ttm-ticket-detail/ttm-ticket-detail.component';
import {TtmTicketCardComponent} from './components/ttm-ticket-card/ttm-ticket-card.component';
import {ImageDrawerComponent} from './components/image-drawer/image-drawer.component';
import {WorkorderImagesComponent} from './components/workorder-images/workorder-images.component';


register();
registerLocaleData(localeNl, 'nl-NL');

declare var Hammer: any;

export class MyHammerConfig extends HammerGestureConfig {
    override buildHammer(element: HTMLElement) {
        let mc = new Hammer(element, {
            touchAction: 'pan'
        });
        return mc;
    }
}

export const NL_DATE_FORMATS = {
    parse: {
        dateInput: {year: 'numeric', month: '2-digit', day: '2-digit'},
    },
    display: {
        dateInput: {year: 'numeric', month: '2-digit', day: '2-digit'},
        monthYearLabel: {year: 'numeric', month: 'short'},
        dateA11yLabel: {year: 'numeric', month: 'long', day: 'numeric'},
        monthYearA11yLabel: {year: 'numeric', month: 'long'},
    },
};

@NgModule({
    declarations: [
        AppComponent,
        ConnectivityBannerComponent,
        PwaInstallBannerComponent,
        ConfirmDialogComponent,
        GoogleMapsComponent,
        SvgMapComponent,
        DataConverterComponent,
        FieldErrorComponent,
        LinkLoginComponent,
        WorkordersComponent,
        CodaltComponent,
        ConvertGeometryPipe,
        ConvertPolygonPipe,
        SvgOverlayComponent,
        EntitiesComponent,
        SafeSrcPipe,
        UsersComponent,
        UserEditComponent,
        AuthRolesComponent,
        UserFieldDialogComponent,
        TtmTicketDialogComponent,
        TtmTicketsComponent,
        FilterPipe,
        HasRolePipe,
        ByIdPipe,
        GeoLatLngPipe,
        SvgAsImgComponent,
        ProjectsMapComponent,
        TtmDetailsDialogComponent,
        TtmStatusDialogComponent,
        DrawingsComponent,
        ImportDrawingDialogComponent,
        HasFeaturePipe,
        IndeterminatePipe,
        MarkerPipe,
        CustomMarkerPipe,
        IntersectsPipe,
        EntityInBoundsPipe,
        EntityZoomPipe,
        TtmTicketTypePipe,
        PresentTtmTypesPipe,
        PresentTtmStatusesPipe,
        CountEnabledStatusesPipe,
        CountEnabledTtmTypesPipe,
        ClusterTtmsPipe,
        TtmDetailsComponent,
        CodaltImageComponent,
        SafeBgUrlPipe,
        ImageViewerComponent,
        TtmSvgViewerComponent,
        WorkorderDetailComponent,
        TtmMergeComponent,
        TtmSplitComponent,
        CodaltDatePipe,
        FileUploadFailDialogComponent,
        MapMutatorComponent,
        TextOverlayComponent,
        WhereInPipe,
        CountEnabledFgItemsPipe,
        TtmStatusPipe,
        WorkorderComposeComponent,
        TSNedMarkerPipe,
        DrawingNamePipe,
        DecimalInputDirective,
        OverrideValuesPipe,
        TtmHistoryDialogComponent,
        DashedLinePipe,
        GndLinePipe,
        CountAmountOfTtmsPipe,
        Nl2brPipe,
        ChangeTtmAmountDialogComponent,
        ExportInterfaceComponent,
        TtmTicketDepartmentPipe,
        TtmTicketStatusPipe,
        IsDateBeforePipe,
        IfNotNullPipe,
        OrderDrawingsPipe,
        UserInsightComponent,
        SumPipe,
        TtmTicketDetailComponent,
        TtmTicketCardComponent,
        ImageDrawerComponent,
        WorkorderImagesComponent,
    ],
    imports: [
        BrowserModule,
        CommonModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatMenuModule,
        MatProgressBarModule,
        MatInputModule,
        MatRadioModule,
        MatDialogModule,
        GoogleMapsModule,
        MatButton,
        NgSelectComponent,
        NgOptionComponent,
        MatListModule,
        MatSelectModule,
        MatOption,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatCheckboxModule,
        MatExpansionModule,
        MatChip,
        MatIconModule,
        MatButtonModule,
        MatTooltip,
        MatRipple,
        MatBadge,
        MatProgressSpinner,
        MatDatepickerModule,
        MatNativeDateModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        }),
        NgOptionTemplateDirective,
        MatSlideToggle,
        GenericModule,
        HammerModule,
        SafeHtmlPipe,
        NgLabelTemplateDirective
    ],
    providers: [
        {provide: ErrorHandler, useClass: CustomErrorHandlerService},
        {provide: LOCALE_ID, useValue: 'nl-NL'},
        {provide: MAT_DATE_LOCALE, useValue: 'nl-NL'},
        {provide: MAT_DATE_FORMATS, useValue: NL_DATE_FORMATS},
        {provide: HTTP_INTERCEPTORS, useClass: CodaltInterceptor, multi: true},
        {provide: MatPaginatorIntl, useValue: getDutchPaginatorIntl()},
        DatePipe,
        ConfirmDialogService,
        AuthenticationService,
        EntityService,
        CustomerService,
        ProjectService,
        WorkorderService,
        TtmTicketService,
        provideAnimations(),
        ApiService,
        CanDeactivate,
        {provide: HAMMER_GESTURE_CONFIG, useClass: MyHammerConfig}
    ],
    bootstrap: [AppComponent],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class AppModule {
}
